import { Injectable, inject, signal } from '@angular/core';
import { ENV_CONFIG } from '@core/config/env.token';
import { STORAGE_KEYS } from '@shared/constants/storage-keys';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type LoggerThreshold = LogLevel | 'none';

export interface DebugLogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  message: string;
  requestId?: string;
  context?: Record<string, unknown>;
}

/**
 * Servicio simple de logging persistente que almacena los eventos de depuración en localStorage
 * para mantener un historial incluso al navegar entre pantallas.
 */
@Injectable({ providedIn: 'root' })
export class LogService {
  private readonly envConfig = inject(ENV_CONFIG, { optional: true });
  private readonly threshold = this.resolveThreshold();
  private readonly enabled = !(this.envConfig?.production ?? false) && this.threshold !== 'none';
  private readonly storageKey = STORAGE_KEYS.debugLog;
  private readonly maxEntries = 500;
  private readonly entries = signal<DebugLogEntry[]>([]);

  readonly logEntries = this.entries.asReadonly();

  constructor() {
    if (!this.enabled) {
      this.clearPersistedEntries();
      return;
    }
    this.entries.set(this.loadEntries());
  }

  debug(message: string, context?: Record<string, unknown>): DebugLogEntry {
    return this.log('debug', message, context);
  }

  info(message: string, context?: Record<string, unknown>): DebugLogEntry {
    return this.log('info', message, context);
  }

  warn(message: string, context?: Record<string, unknown>): DebugLogEntry {
    return this.log('warn', message, context);
  }

  error(message: string, context?: Record<string, unknown>): DebugLogEntry {
    return this.log('error', message, context);
  }

  log(level: LogLevel, message: string, context?: Record<string, unknown>): DebugLogEntry {
    const clonedContext = context ? this.cloneContext(context) : undefined;
    const requestId = typeof clonedContext?.['requestId'] === 'string'
      ? clonedContext['requestId']
      : undefined;
    const entry: DebugLogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
      level,
      message,
      requestId,
      context: clonedContext ? this.redactSensitiveContext(clonedContext) : undefined
    };

    if (!this.enabled || !this.shouldLog(level)) {
      return entry;
    }

    this.entries.update(current => {
      const updated = [...current, entry];
      if (updated.length > this.maxEntries) {
        updated.splice(0, updated.length - this.maxEntries);
      }
      return updated;
    });

    this.persist();
    this.forwardToConsole(entry);
    return entry;
  }

  getEntries(): DebugLogEntry[] {
    return [...this.entries()];
  }

  clear(reason?: string): void {
    if (!this.enabled) {
      this.clearPersistedEntries();
      return;
    }

    this.entries.set([]);
    this.persist();
    if (reason) this.forwardToConsole({ id: 'system', timestamp: new Date().toISOString(), level: 'info', message: 'Log limpiado', context: { reason } });
  }

  private loadEntries(): DebugLogEntry[] {
    if (!this.enabled) {
      return [];
    }

    if (typeof localStorage === 'undefined') {
      return [];
    }

    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as DebugLogEntry[];
      if (Array.isArray(parsed)) {
        return parsed;
      }
      return [];
    } catch {
      return [];
    }
  }

  private persist(): void {
    if (!this.enabled) {
      return;
    }

    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      const serialized = JSON.stringify(this.entries(), (_key, value) => {
        if (value instanceof Error) {
          return { name: value.name, message: value.message, stack: value.stack };
        }
        return value;
      });
      localStorage.setItem(this.storageKey, serialized);
    } catch {
      // Ignorar errores de persistencia en almacenamiento local.
    }
  }

  private cloneContext(context: Record<string, unknown>): Record<string, unknown> {
    try {
      return JSON.parse(JSON.stringify(context)) as Record<string, unknown>;
    } catch {
      return { serialized: 'No se pudo serializar el contexto' };
    }
  }

  private forwardToConsole(entry: DebugLogEntry): void {
    if (!this.enabled) {
      return;
    }

    const payload = entry.context ? [entry.message, entry.context] : [entry.message];
    switch (entry.level) {
      case 'warn':
        console.warn('[LogService]', ...payload);
        break;
      case 'error':
        console.error('[LogService]', ...payload);
        break;
      case 'debug':
        console.debug('[LogService]', ...payload);
        break;
      default:
        console.log('[LogService]', ...payload);
        break;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    const priority: Record<LoggerThreshold, number> = {
      debug: 10,
      info: 20,
      warn: 30,
      error: 40,
      none: 100
    };

    return priority[level] >= priority[this.threshold];
  }

  private resolveThreshold(): LoggerThreshold {
    const configured = this.envConfig?.logLevel;
    if (configured) {
      return configured;
    }

    return this.envConfig?.production ? 'none' : 'debug';
  }

  private clearPersistedEntries(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.removeItem(this.storageKey);
  }

  private redactSensitiveContext(context: Record<string, unknown>): Record<string, unknown> {
    const sensitiveKeyMatchers = [
      'payload',
      'location',
      'latitude',
      'longitude',
      'precision',
      'deviceinfo',
      'fingerprint',
      'image',
      'apikey',
      'authorization',
      'token'
    ];

    const visit = (value: unknown, key?: string): unknown => {
      if (key && sensitiveKeyMatchers.some(matcher => key.toLowerCase().includes(matcher))) {
        return '[REDACTED]';
      }

      if (Array.isArray(value)) {
        return value.map(item => visit(item));
      }

      if (value && typeof value === 'object') {
        return Object.fromEntries(
          Object.entries(value as Record<string, unknown>).map(([entryKey, entryValue]) => [
            entryKey,
            visit(entryValue, entryKey)
          ])
        );
      }

      if (typeof value === 'string' && value.length > 180) {
        return `${value.slice(0, 60)}...[truncated]`;
      }

      return value;
    };

    return visit(context) as Record<string, unknown>;
  }
}
